import React, { useContext, useMemo } from 'react';
import {
  AuthenticationSettingsContext,
  AuthenticationSettingsContextProps,
  authenticationSettingsSlice,
} from '../context/AuthenticationSettingsContext';
import { CompanyContext } from '../context/CompanyContext';
import { AuthenticationSettings } from '../types/authentication_settings/authenticationSettings';
import { useAppDispatch, useAppSelector } from '../ui/pages/store';

interface AuthenticationSettingsProviderProps {
  children: React.ReactNode;
}

export default function AuthenticationSettingsProvider({ children }: AuthenticationSettingsProviderProps) {
  const { currentCompany } = useContext(CompanyContext);

  const dispatch = useAppDispatch();
  const items = useAppSelector(state => state.authenticationSettings.items);
  const loading = useAppSelector(state => state.authenticationSettings.loading);

  const authenticationSettings = useMemo(() => {
    return new Map(items.map(item => [item.id, new AuthenticationSettings(item)]));
  }, [items]);

  const authenticationSettingsContext: AuthenticationSettingsContextProps = {
    authenticationSettings,
    authenticationSettingsLoading: loading,
    companyAuthenticationSettings: [...authenticationSettings.values()].find(
      as => as.companyId === currentCompany.id && !as.userId,
    ),
    authenticationSettingsForUser: (userId: string) => {
      return [...authenticationSettings.values()].find(
        as => as.companyId === currentCompany.id && as.userId === userId,
      );
    },
    setAuthenticationSettings: (authenticationSettings: Map<string, AuthenticationSettings>) =>
      dispatch(authenticationSettingsSlice.actions.set([...authenticationSettings.values()])),
  };

  return (
    <AuthenticationSettingsContext.Provider value={authenticationSettingsContext}>
      {children}
    </AuthenticationSettingsContext.Provider>
  );
}
